import { useCallback, useEffect, useRef } from 'react';

/** Where the bottom-sheet layout applies (ds.css docks dialogs below `sm`). */
const SHEET_QUERY = '(max-width: 39.9375rem)';
/** A drag past this closes the sheet on release. */
const DISMISS_PX = 120;
/**
 * A shorter, fast flick closes it too: speed in px per ms, measured over the
 * last moments before release (a flick is fast at the end, not on average).
 */
const FLICK_SPEED = 0.5;
const FLICK_MIN_PX = 40;
const FLICK_WINDOW_MS = 100;

/**
 * Swipe a bottom sheet down to close it, as on every phone OS.
 *
 * The sheet follows the finger from its handle or header (never from the
 * body, which scrolls, and never from a control in the header); released
 * far enough or flicked, it slides away and `onDismiss` runs, otherwise it
 * springs back. Returns a ref for the handle element; the listeners go on the
 * dialog that contains it. Listeners are passive and the transform is written
 * directly, so a drag never re-renders the form inside.
 */
export function useSheetSwipe(enabled: boolean, onDismiss: () => void) {
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  return useCallback(
    (handle: HTMLElement | null) => {
      const dialog = handle?.closest<HTMLElement>('[role="dialog"]');
      if (!enabled || !handle || !dialog) return;

      let startY = 0;
      let distance = 0;
      /** Recent [time, distance] samples for the release speed. */
      let samples: Array<[number, number]> = [];
      let dragging = false;

      const onStart = (e: TouchEvent) => {
        if (e.touches.length !== 1 || !window.matchMedia(SHEET_QUERY).matches)
          return;
        const target = e.target as Element;
        const grabbed =
          handle.contains(target) ||
          (target.closest('.modal__header') &&
            !target.closest(
              'button, a, input, textarea, select, [role="tab"]',
            ));
        if (!grabbed) return;
        dragging = true;
        distance = 0;
        startY = e.touches[0].clientY;
        samples = [[e.timeStamp, 0]];
        dialog.style.transition = 'none';
      };

      const onMove = (e: TouchEvent) => {
        if (!dragging) return;
        distance = Math.max(0, e.touches[0].clientY - startY);
        const now = e.timeStamp;
        samples.push([now, distance]);
        while (samples.length > 2 && now - samples[0][0] > FLICK_WINDOW_MS) {
          samples.shift();
        }
        dialog.style.transform = `translate3d(0, ${distance}px, 0)`;
      };

      const onEnd = (e: TouchEvent) => {
        if (!dragging) return;
        dragging = false;
        const [fromAt, fromDistance] = samples[0];
        const speed =
          (distance - fromDistance) / Math.max(1, e.timeStamp - fromAt);
        const dismiss =
          distance > DISMISS_PX ||
          (distance > FLICK_MIN_PX && speed > FLICK_SPEED);
        if (dismiss) {
          dialog.style.transition = 'transform 160ms ease-in';
          dialog.style.transform = 'translate3d(0, 100%, 0)';
          window.setTimeout(() => onDismissRef.current(), 140);
        } else {
          dialog.style.transition = 'transform 200ms ease-out';
          dialog.style.transform = '';
        }
      };

      dialog.addEventListener('touchstart', onStart, { passive: true });
      dialog.addEventListener('touchmove', onMove, { passive: true });
      dialog.addEventListener('touchend', onEnd, { passive: true });
      dialog.addEventListener('touchcancel', onEnd, { passive: true });
      return () => {
        dialog.removeEventListener('touchstart', onStart);
        dialog.removeEventListener('touchmove', onMove);
        dialog.removeEventListener('touchend', onEnd);
        dialog.removeEventListener('touchcancel', onEnd);
      };
    },
    [enabled],
  );
}
